<template>
    <div>
        <!-- 剪贴板读不到时的兜底：手工粘贴 -->
        <re-b-modal ref="pasteModal" header="粘贴菜单配置 JSON"
                    @ok="parseMenuJson(pastedJson)"
                    @hide="pastedJson = null">
            <mini-input type="textarea" class="m-auto"
                        :value.sync="pastedJson"
                        without-label></mini-input>
        </re-b-modal>

        <!-- 导入前确认：展示解析出的菜单树 -->
        <re-b-modal ref="previewModal" large :header="previewHeader" ok-label="导入"
                    :ok-loading="uploading" no-auto-hide
                    @ok="uploadMenus">
            <vue-json-pretty :data="parsedMenus" :deep="4"></vue-json-pretty>
        </re-b-modal>
    </div>
</template>

<script>
/**
 * 菜单导入弹窗：把一份菜单 JSON 挂到指定父菜单下。
 * <p>
 * 交互与 /query/config/table 的 Import 一致：先读剪贴板，读不到（浏览器不支持 / 用户拒绝授权 /
 * 内容为空 / 不是合法 JSON）就弹输入框让人手工粘贴，解析成功后先预览、确认再上传。
 * <p>
 * 只处理 routerCardItem（路由卡项目，平级菜单入口）与 menuRouter（面包屑菜单，子级路由）两类，
 * 其余类型（project / routerCard / routerCardSetting / editCard / aclPoint）不由导入维护，
 * 遇到就整单拒绝，避免从这个入口悄悄造出权限点或路由卡。
 * <p>
 * level 由后端 EmLevelNode#beforeSave 依据 parentId 自行计算，JSON 里不带、也不该带。
 *
 * Author Claude
 * Date 2026-09-14
 */
import ReBModal from "@/components/echarts-part/CalendarShift/cells/stuffs/ReBModal.vue";
import MiniInput from "@/components/common/inputs/MiniInput.vue";
import VueJsonPretty from 'vue-json-pretty';

// 由本导入维护的菜单类型，见 EmMenuTypeEnum
const SUPPORTED_MENU_TYPES = ['routerCardItem', 'menuRouter'];

// 允许写入的字段；id / parentId / level 以及各种 xxxName 回显字段一律忽略，
// 前者由导入过程自己决定，后者根本不是 EmMenuSaveForm 的字段。
const IMPORT_FIELDS = ['name', 'menuTypeCode', 'crudCode', 'i18nMenuName', 'i18nBreadcrumbName',
    'url', 'baseRequestUrl', 'aclUrl', 'iconCode', 'seq', 'status', 'remark', 'customConfigJson'];

export default {
    name: "EmMenuImportModal",
    components: {ReBModal, MiniInput, VueJsonPretty},
    props: {
        // 导入落到哪个父菜单下面：列表页左侧菜单树当前选中的节点
        parentId: {
            type: [String, Number],
            default: ''
        }
    },
    data() {
        return {
            pastedJson: null,
            parsedMenus: null,
            uploading: false,
        }
    },
    computed: {
        previewHeader() {
            return `确认导入到父菜单（id=${this.parentId}）下`;
        }
    },
    methods: {
        //------------------- 入口 ----------------------
        /**
         * 识别剪贴板内容；识别不出就弹输入框。
         * 注意 readText() 需要用户手势 + 剪贴板读取授权，被拒同样落到输入框。
         */
        start() {
            if (!this.checkParent()) {
                return;
            }
            if (!navigator.clipboard || !navigator.clipboard.readText) {
                this.$refs.pasteModal.showModal();
                return;
            }
            navigator.clipboard.readText()
                .then(text => (text && text.trim())
                    ? this.parseMenuJson(text)
                    : this.$refs.pasteModal.showModal())
                .catch(() => this.$refs.pasteModal.showModal());
        },
        //------------------- 解析 / 校验 ----------------------
        parseMenuJson(json) {
            let parsed;
            try {
                parsed = JSON.parse(json);
            } catch (error) {
                this.showDanger('内容不是合法 JSON：' + error);
                this.$refs.pasteModal.showModal();
                return;
            }
            let rows = Array.isArray(parsed) ? parsed : [parsed];
            let invalid = this.collectInvalidNodes(rows, []);
            if (invalid.length > 0) {
                this.showDanger('以下菜单项不受导入支持（仅支持 '
                    + SUPPORTED_MENU_TYPES.join(' / ') + '）：' + invalid.join('、'));
                return;
            }
            if (rows.length === 0) {
                this.showDanger('没有可导入的菜单项');
                return;
            }
            this.parsedMenus = rows;
            this.$refs.previewModal.showModal();
        },
        collectInvalidNodes(nodes, invalid) {
            (nodes || []).forEach(node => {
                let label = (node && node.name) || (node && node.url) || JSON.stringify(node);
                if (!node || !node.name) {
                    invalid.push(`${label}（缺少 name）`);
                } else if (!SUPPORTED_MENU_TYPES.includes(node.menuTypeCode)) {
                    invalid.push(`${label}（menuTypeCode=${node.menuTypeCode}）`);
                }
                node && this.collectInvalidNodes(node.children, invalid);
            });
            return invalid;
        },
        //------------------- 上传 ----------------------
        uploadMenus() {
            if (!this.checkParent()) {
                return;
            }
            this.uploading = true;
            this.saveNodes(this.parsedMenus, this.parentId)
                .then(count => {
                    this.$refs.previewModal.hide();
                    this.parsedMenus = null;
                    this.showSuccess(`菜单导入成功，共 ${count} 项`);
                    this.$emit('imported');
                })
                .catch(error => this.showDanger('菜单导入失败：'
                    + ((error && error.message) ? error.message : error)))
                .then(() => this.uploading = false);
        },
        /**
         * 串行保存：子节点的 parentId 依赖父节点保存后返回的 id，不能并发。
         * 中途失败即中断，已建好的节点保留（菜单是幂等可手工收拾的配置数据，
         * 强行回滚反而容易把别人的菜单删掉）。
         */
        saveNodes(nodes, parentId) {
            return (nodes || []).reduce((chain, node) => chain.then(count =>
                this.saveNode(node, parentId)
                    .then(id => this.saveNodes(node.children, id))
                    .then(subCount => count + 1 + subCount)
            ), Promise.resolve(0));
        },
        saveNode(node, parentId) {
            let entity = {parentId};
            IMPORT_FIELDS.forEach(key => {
                if (node[key] !== undefined && node[key] !== null) {
                    entity[key] = node[key];
                }
            });
            return this.$store.dispatch('api/put', {
                url: `${process.env.VUE_APP_EM_ADMIN_URL}/emMenu`,
                entity,
                module: 'emMenu',
                commonErrorHandle: false,
            }).then(saved => {
                if (!saved || !saved.id) {
                    return Promise.reject(new Error(`「${node.name}」保存后未返回 id`));
                }
                return saved.id;
            });
        },
        //------------------- 提示 ----------------------
        checkParent() {
            if (this.parentId === null || this.parentId === undefined || `${this.parentId}` === '') {
                this.showDanger('请先在左侧菜单树中选择要导入到的父菜单');
                return false;
            }
            return true;
        },
        showSuccess(message) {
            this.$store.dispatch('base/showSuccess', {
                userAlertSuccessStr: message,
                dismissCountDown: 5,
                userAlertSuccessVisible: false,
            }, {root: true});
        },
        showDanger(message) {
            this.$store.dispatch('base/showDanger', {
                userAlertDangerStr: message,
                userAlertDangerVisible: false,
            }, {root: true});
        },
    }
}
</script>
